/**
 * The admin-defined `/` script line kinds (F-17-type, 2026-09-04), as every
 * surface that renders or offers them reads them: a module SINGLETON, loaded
 * once from `GET /api/text-kinds` and refreshed whenever a board opens or an
 * admin edits the list.
 *
 * Why a singleton and not a per-board store: kinds are WORKSPACE-wide (one
 * registry, every board), and the two editors that offer them (the grid's
 * cell editor and the script block) sit deep in trees that have no prop path
 * from the app shell — exactly the situation the F-27 translate store is in
 * (features/translate/store.ts). The `/` menu options are derived from this
 * store (ui/text-type.ts `textTypeOptions`), and the kinds' INK is applied
 * through one injected stylesheet (./styles.ts), so a cell renderer never has
 * to look a kind up at all: it wears the class `tt-k-<id>` and the stylesheet
 * says what that means.
 *
 * A kind this client has not loaded (a peer's brand-new one) renders as
 * bracketed plain ink until the next refresh — degraded, never dropped.
 *
 * NO NETWORK IMPORT HERE, on purpose: the fetch lives in ./load.ts. The two
 * editing surfaces read this store, and the server's e2e harnesses typecheck
 * the client's grid module for one type (`GridLayoutSnapshot`) — which pulls
 * whatever the component imports into a NODE program. `core/api.ts` reads
 * `import.meta.env`, a Vite-only global, so a component reaching it that way
 * breaks `tsc -p tsconfig.e2e.json` in @kola/server. Keeping the state pure
 * and the loader separate means a renderer never drags fetch into that graph.
 */
import type { TextKind } from "@kola/shared";
import { useStore } from "zustand";
import { createStore, type StoreApi } from "zustand/vanilla";

import { applyTextKindStyles } from "./styles.js";

export interface TextKindsState {
  kinds: readonly TextKind[];
  /** True once the first load (or a later refresh) has answered. */
  loaded: boolean;
  /** Replace the list — also re-applies the injected stylesheet. */
  setKinds(kinds: readonly TextKind[]): void;
}

export type TextKindsStore = StoreApi<TextKindsState>;

export const textKinds: TextKindsStore = createStore<TextKindsState>((set) => ({
  kinds: [],
  loaded: false,
  setKinds: (kinds) => {
    applyTextKindStyles(kinds);
    set({ kinds: [...kinds], loaded: true });
  },
}));

/** The kinds, live — for the `/` menus and the admin page. */
export function useTextKinds(): readonly TextKind[] {
  return useStore(textKinds, (s) => s.kinds);
}

/** One kind by its stored id, or undefined for a built-in / unknown one. */
export function textKindById(id: string): TextKind | undefined {
  return textKinds.getState().kinds.find((kind) => kind.id === id);
}
